import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { EntityInstance } from '../types';
import { useEntityAnalysisDetailsQueries } from '../queries/useEntityAnalysisDetailsQueries';

interface UseEntityQueryOrchestrationProps {
  selectedEntity?: EntityInstance;
  setSelectedEntity: Dispatch<SetStateAction<EntityInstance | undefined>>;
  isEntityModalOpen: boolean;
}

interface UseEntityQueryOrchestrationReturn {
  isValidatingSuppress: boolean;
}

/**
 * Manages query orchestration for entity analysis with debouncing.
 * Handles the timing and execution of entity-related queries, debouncing
 * hover-triggered queries to prevent excessive API calls.
 */
export const useEntityQueryOrchestration = ({
  selectedEntity,
  setSelectedEntity,
  isEntityModalOpen,
}: UseEntityQueryOrchestrationProps): UseEntityQueryOrchestrationReturn => {
  // Query orchestration state
  const [queryDebounceTimer, setQueryDebounceTimer] = useState<
    NodeJS.Timeout | undefined
  >();
  const [shouldRunQueries, setShouldRunQueries] = useState(false);
  const [queryingEntityId, setQueryingEntityId] = useState<
    string | undefined
  >();

  // Debounce logic for hover-triggered queries
  useEffect(() => {
    // Skip if no active entity or if queries are already running for this entity
    // If modal is open, queries were already triggered, no debounce needed
    if (!selectedEntity?.entityId || isEntityModalOpen) {
      return;
    }

    // Clear any existing debounce timer
    if (queryDebounceTimer) {
      clearTimeout(queryDebounceTimer);
    }

    const targetEntityId = selectedEntity.entityId;

    // Start new debounce timer (300ms)
    const timer = setTimeout(() => {
      setShouldRunQueries(true);
      setQueryingEntityId(targetEntityId);
      // Set timeoutId on selectedEntity to trigger queries
      setSelectedEntity((prevState) => {
        if (prevState?.entityId !== targetEntityId) {
          return prevState;
        }
        return {
          ...prevState,
          timeoutId: uuidv4(),
        };
      });
    }, 300);

    setQueryDebounceTimer(timer);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // Note: queryDebounceTimer intentionally omitted from deps to avoid infinite loop
  }, [selectedEntity?.entityId, isEntityModalOpen, setSelectedEntity]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (queryDebounceTimer) {
        clearTimeout(queryDebounceTimer);
      }
    };
  }, [queryDebounceTimer]);

  // Call useEntityAnalysisDetailsQueries with proper entity tracking
  // Enable queries when:
  // 1. Debounce completed for hover (shouldRunQueries && matching entityId), OR
  // 2. Modal is open (direct click on row without hover)
  const queriesEnabledForHover =
    shouldRunQueries && queryingEntityId === selectedEntity?.entityId;
  const queriesEnabledForModal =
    isEntityModalOpen && !!selectedEntity?.entityId;

  const { isValidatingSuppress } = useEntityAnalysisDetailsQueries({
    selectedEntity:
      queriesEnabledForHover || queriesEnabledForModal
        ? selectedEntity
        : undefined,
    enabled: queriesEnabledForHover || queriesEnabledForModal,
    setSelectedEntity,
  });

  // Reset query state when entity changes (but let in-flight queries complete)
  useEffect(() => {
    if (selectedEntity?.entityId !== queryingEntityId) {
      // Don't stop in-flight queries, just mark that we need new queries for this entity
      setShouldRunQueries(false);
    }
  }, [selectedEntity?.entityId, queryingEntityId]);

  return {
    isValidatingSuppress,
  };
};
